/** PLANS DE SOUDURE™ — all customer-facing content (French) and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/1bfcbe78-8a7f-40a8-98d9-269de73f0a57/`

export const PRODUCT = {
  name: 'PLANS DE SOUDURE™',
  descriptor: 'Plans de fabrication d’équipements d’atelier pour soudeurs et métalliers',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'DROITS DE FABRICATION COMMERCIALE INCLUS',
  sentence: 'Fabriquez pour votre propre atelier — ou fabriquez et vendez les produits finis.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 PROJETS D’ATELIER',
  '6 VOLUMES NUMÉRIQUES',
  'COTES & LISTES DE DÉBIT',
  '5 BONUS OFFERTS',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Aperçu', href: '#project-preview' },
  { label: 'Dans les plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonus', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** Every plan page and volume cover is a 4:5 WebP, 1100 px wide. */
const PAGE_SIZE = { width: 1100, height: 1376 }

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792861083549_ps-hero-v2-3f14a7e2.webp`,
  alt: 'Collection PLANS DE SOUDURE™ : six volumes sur une table d’atelier, une tablette affichant un plan coté et un badge 5 bonus offerts',
  width: 1100,
  height: 1138,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 projets d’atelier', icon: 'projects' },
  { label: '6 volumes organisés', icon: 'volumes' },
  { label: 'Cotes + listes de débit', icon: 'dimensions' },
  { label: '5 bonus offerts', icon: 'bonus' },
]

/** Project P26, Volume 05, PDF pages 29–34 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P26',
  name: 'Poste d’ébavurage & de préparation de surface',
  volume: 'Volume 05',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792861157464_ps-p26-01-overview-f03c1ebd.webp`,
    caption: 'Le projet terminé',
    alt: 'Volume 05 : P26 Poste d’ébavurage & de préparation de surface, vue d’ensemble de l’établi de finition tout acier',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861162765_ps-p26-02-plans-fa1d20a3.webp`,
    caption: 'Plans cotés — pouces & millimètres',
    alt: 'Volume 05 : P26 plans cotés — vues de face, de droite, de dessus et isométrique en pouces et en millimètres',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861168516_ps-p26-03-cutlist-e2efe344.webp`,
    caption: 'Liste de débit & vue éclatée',
    alt: 'Volume 05 : P26 liste de débit des pièces A à F en pouces et en millimètres, avec la vue éclatée',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861173977_ps-p26-04-sequence-582ddd12.webp`,
    caption: 'Séquence de fabrication illustrée',
    alt: 'Volume 05 : P26 séquence de fabrication illustrée en six étapes, du débit à l’établi terminé',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861179674_ps-p26-05-welds-9702996d.webp`,
    caption: 'Emplacements des soudures & mise en position',
    alt: 'Volume 05 : P26 emplacements des soudures et détails de mise en position',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861185632_ps-p26-06-reference-a8a92dca.webp`,
    caption: 'Référence du projet fini',
    alt: 'Volume 05 : P26 référence de fabrication terminée avec dimensions générales et nombre de pièces',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = [
  'Prévoyez les matériaux.',
  'Voyez comment les pièces s’assemblent.',
  'Suivez la séquence de fabrication.',
]

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792861191008_ps-tech-01-p02-plan-7d16a8ac.webp`,
    title: 'CONNAÎTRE LES COTES',
    project: 'Projet 02 · Paire de tréteaux de fabrication · Volume 01',
    description: 'Vérifiez les dimensions finies avant la coupe et l’assemblage.',
    alt: 'Volume 01 : Projet 02 Paire de tréteaux de fabrication, plan coté 3 vues',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861196778_ps-tech-02-p05-cutlist-733e89cf.webp`,
    title: 'SAVOIR QUOI COUPER',
    project: 'Projet 05 · Piédestal porte-câbles & torche · Volume 01',
    description: 'Consultez les pièces, les quantités, les profilés et les cotes de débit.',
    alt: 'Volume 01 : Projet 05 Piédestal porte-câbles & torche, vue éclatée et liste de débit complète des pièces A à E',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861202735_ps-tech-03-p03-assembly-de0fb637.webp`,
    title: 'VOIR COMMENT ÇA S’ASSEMBLE',
    project: 'Projet 03 · Servante à galet réglable · Volume 01',
    description: 'Découvrez les assemblages, les soudures et les détails de la tête à galet.',
    alt: 'Volume 01 : Projet 03 Servante à galet réglable, assemblages, soudures et détails de fabrication',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792861208297_ps-tech-04-p04-sequence-7d1fca3e.webp`,
    title: 'SUIVRE LA FABRICATION',
    project: 'Projet 04 · Cadre de rideau de soudage autoportant · Volume 01',
    description: 'Suivez la séquence illustrée, des pièces débitées au cadre assemblé.',
    alt: 'Volume 01 : Projet 04 Cadre de rideau de soudage autoportant, séquence de fabrication en huit étapes',
    ...PAGE_SIZE,
  },
]

const BONUS_SIZE = { width: 800, height: 800 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Essentiels d’atelier & supports',
    projects: '6 projets',
    description: 'Piédestaux, tréteaux, servantes à galet et équipements de support pratiques pour l’atelier.',
    projectList: [
      'P01 — Piédestal pour étau d’établi',
      'P02 — Paire de tréteaux de fabrication',
      'P03 — Servante à galet réglable',
      'P04 — Cadre de rideau de soudage autoportant',
      'P05 — Piédestal porte-câbles & torche',
      'P06 — Support d’inspection & de repos des pièces',
    ],
    src: `${IMG}1792861124036_ps-vol-1-83b9c71d.webp`,
    alt: 'Couverture du volume 01 : Essentiels d’atelier & supports',
    ...PAGE_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Tables de soudure & établis',
    projects: '5 projets',
    description: 'Tables de soudure fixes et établis en acier pour la fabrication.',
    projectList: [
      'P07 — Table de soudure fixe compacte',
      'P08 — Établi de fabrication à plateau acier',
      'P09 — Table de soudure à rainures pour bridage',
      'P10 — Établi de soudure extensible',
      'P11 — Établi de soudure d’angle',
    ],
    src: `${IMG}1792861129473_ps-vol-2-14c8bdb5.webp`,
    alt: 'Couverture du volume 02 : Tables de soudure & établis',
    ...PAGE_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Chariots de soudure & postes mobiles',
    projects: '5 projets',
    description: 'Chariots d’équipement et postes mobiles pour la soudure et la préparation.',
    projectList: [
      'P12 — Chariot de soudure compact mono-poste',
      'P13 — Chariot de soudure polyvalent porte-bouteille',
      'P14 — Chariot pour deux postes de soudure',
      'P15 — Chariot pour découpeur plasma',
      'P16 — Poste de fabrication mobile',
    ],
    src: `${IMG}1792861135541_ps-vol-3-4fe24a5c.webp`,
    alt: 'Couverture du volume 03 : Chariots de soudure & postes mobiles',
    ...PAGE_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Rangement & racks à matériaux',
    projects: '5 projets',
    description: 'Rangez barres d’acier, tôles, chutes, serre-joints et outils à main.',
    projectList: [
      'P17 — Rack horizontal pour barres d’acier',
      'P18 — Rack vertical pour tubes & chutes courtes',
      'P19 — Rack à fentes pour tôles',
      'P20 — Rack de tri des chutes d’acier',
      'P21 — Rack mural pour serre-joints & outils',
    ],
    src: `${IMG}1792861140698_ps-vol-4-8b3bd407.webp`,
    alt: 'Couverture du volume 04 : Rangement & racks à matériaux',
    ...PAGE_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Postes d’outillage & de préparation',
    projects: '5 projets',
    description: 'Postes dédiés à la coupe, au meulage, au perçage, au traçage et à la finition.',
    projectList: [
      'P22 — Poste pour tronçonneuse à métaux',
      'P23 — Piédestal pour touret à meuler',
      'P24 — Poste support pour perceuse à colonne',
      'P25 — Poste de traçage & de mesure',
      'P26 — Poste d’ébavurage & de préparation de surface',
    ],
    src: `${IMG}1792861146169_ps-vol-5-053d96f1.webp`,
    alt: 'Couverture du volume 05 : Postes d’outillage & de préparation',
    ...PAGE_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Systèmes d’atelier intégrés',
    projects: '4 projets',
    description: 'Réunissez zones de travail, préparation, rangement et manutention des matériaux.',
    projectList: [
      'P27 — Îlot de fabrication à deux zones',
      'P28 — Poste de service mural pleine longueur',
      'P29 — Ligne de coupe & de préparation des matériaux',
      'P30 — Poste de fabrication portable pour chantier',
    ],
    src: `${IMG}1792861151990_ps-vol-6-f91afb3b.webp`,
    alt: 'Couverture du volume 06 : Systèmes d’atelier intégrés',
    ...PAGE_SIZE,
  },
]

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Fiche d’achat d’acier & de projet',
    description: 'Organisez vos achats d’acier et les notes de chaque projet.',
    src: `${IMG}1792861313560_ps-bonus-01-bd0afa31.webp`,
    alt: 'Couverture du bonus 01 : Fiche d’achat d’acier & de projet',
    ...BONUS_SIZE,
  },
  {
    number: '02',
    title: 'Fiche d’optimisation des débits & de repérage',
    description: 'Planifiez vos coupes et repérez chaque pièce avant l’assemblage.',
    src: `${IMG}1792861318999_ps-bonus-02-9b329abe.webp`,
    alt: 'Couverture du bonus 02 : Fiche d’optimisation des débits & de repérage',
    ...BONUS_SIZE,
  },
  {
    number: '03',
    title: 'Fiche terrain sécurité atelier',
    description: 'Gardez une checklist d’atelier concise toujours à portée de main.',
    src: `${IMG}1792861324188_ps-bonus-03-1485e2d2.webp`,
    alt: 'Couverture du bonus 03 : Fiche terrain sécurité atelier',
    ...BONUS_SIZE,
  },
  {
    number: '04',
    title: 'Guide de préparation de surface & de finition',
    description: 'Organisez la préparation de surface et les étapes de finition.',
    src: `${IMG}1792861329069_ps-bonus-04-e234d777.webp`,
    alt: 'Couverture du bonus 04 : Guide de préparation de surface & de finition',
    ...BONUS_SIZE,
  },
  {
    number: '05',
    title: 'Fiche de calcul du coût de projet',
    description: 'Suivez le coût des matériaux et préparez l’estimation de votre projet.',
    src: `${IMG}1792861334547_ps-bonus-05-fd66e1d9.webp`,
    alt: 'Couverture du bonus 05 : Fiche de calcul du coût de projet',
    ...BONUS_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  'Cotes et vues techniques — chaque mesure en pouces et en millimètres.',
  'Matériaux et listes de débit complètes',
  'Vues éclatées et séquences de fabrication',
]

export const FAQS: Faq[] = [
  {
    question: 'Qu’est-ce que j’achète ?',
    answer:
      'Une collection numérique de plans de fabrication d’équipements d’atelier : 30 projets répartis en six volumes PDF, plus cinq ressources bonus.',
  },
  {
    question: 'À qui s’adresse cette collection ?',
    answer:
      'Aux soudeurs amateurs qui ont déjà de l’expérience, aux métalliers indépendants et aux petits ateliers. Elle s’adresse à des personnes qui savent déjà souder et travailler l’acier.',
  },
  {
    question: 'Est-ce un cours de soudure pour débutants ?',
    answer:
      'Non. La collection porte sur des projets d’équipement d’atelier et leur documentation de fabrication. Elle n’enseigne pas la soudure depuis le début.',
  },
  {
    question: 'S’agit-il de livres papier ou d’équipements ?',
    answer:
      'Le produit est numérique. Vous recevez des fichiers PDF ; les livres papier, l’acier, les outils et les équipements ne sont pas inclus.',
  },
  {
    question: 'Puis-je consulter les fichiers sur tablette ou ordinateur ?',
    answer:
      'Oui. Vous pouvez ouvrir les PDF sur un ordinateur, une tablette ou un téléphone compatible, et imprimer les pages dont vous avez besoin.',
  },
  {
    question: 'Les mesures sont-elles en millimètres ?',
    answer:
      'Oui. Chaque cote est indiquée en pouces et en millimètres, dans les plans comme dans les listes de débit. Vérifiez toujours le profilé réellement acheté avant de couper.',
  },
  {
    question: 'Quels types de projets sont inclus ?',
    answer:
      'Équipements de support, tables de soudure et établis, chariots, racks à matériaux, postes d’outillage et systèmes d’atelier intégrés.',
  },
  {
    question: 'Puis-je voir un aperçu des plans ?',
    answer:
      'Oui. Les six pages du projet P26 — Poste d’ébavurage & de préparation de surface, du Volume 05, sont visibles dans la section aperçu plus haut.',
  },
  {
    question: 'Puis-je vendre ce que je fabrique avec ces plans ?',
    answer:
      'Oui. Votre achat vous autorise à fabriquer les projets pour votre propre atelier ou à vendre les produits physiques que vous réalisez. Vous pouvez imprimer des copies de travail pour votre usage personnel. Vous ne pouvez pas partager, revendre, redistribuer ni concéder en sous-licence les plans numériques, PDF, images ou bonus, originaux ou modifiés.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `Garantie de 7 jours : en cas de problème avec votre achat, écrivez à ${SUPPORT_EMAIL}.`
